<template>
  <v-dialog v-model="dialogVisible" max-width="1400px" persistent scrollable>
    <v-card>
      <v-card-title class="d-flex align-center" style="background-color: #1577da; color: white;">
        <span class="text-h5 font-weight-bold">Mapiranje artikala iz e-računa</span>
        <v-spacer></v-spacer>
        <v-chip color="white" text-color="primary" class="font-weight-bold">
          {{ mappedCount }} / {{ localItems.length }} mapirano
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4" style="max-height: 70vh;">
        <!-- Warehouse and Location Selection -->
        <v-row dense class="mb-4">
          <v-col cols="6" v-if="locations.length > 1">
            <v-autocomplete
              v-model="selectedLocation"
              :items="locations"
              item-text="name"
              item-value="value"
              label="Prodajno mjesto *"
              return-object
              dense
              outlined
              hide-details
              @change="onLocationChange"
            ></v-autocomplete>
          </v-col>
          <v-col :cols="locations.length > 1 ? 6 : 12">
            <v-autocomplete
              v-model="selectedWarehouse"
              :items="warehouses"
              item-text="name"
              item-value="value"
              label="Skladište *"
              return-object
              dense
              outlined
              hide-details
              :disabled="!selectedLocation"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <v-alert v-if="localItems.length === 0" type="info" text>
          Nema artikala za mapiranje.
        </v-alert>

        <v-alert v-if="!selectedWarehouse" type="warning" text class="mb-3">
          Molimo odaberite skladište prije mapiranja artikala.
        </v-alert>

        <v-expansion-panels v-model="openPanels" multiple accordion v-if="selectedWarehouse">
          <v-expansion-panel
            v-for="(item, index) in localItems"
            :key="index"
            :class="{'success lighten-5': item.mapped}"
          >
            <v-expansion-panel-header
              :class="{'focused-item': focusedIndex === index}"
              @click.native="focusedIndex = index"
            >
              <v-row align="center" no-gutters>
                <v-col cols="1">
                  <v-icon :color="item.mapped ? 'success' : 'grey'">
                    {{ item.mapped ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                </v-col>
                <v-col cols="5">
                  <span class="font-weight-medium">
                    {{ item.name }}
                  </span>
                </v-col>
                <v-col cols="2" class="text-center">
                  <v-chip small outlined>{{ item.quantity }} {{ normalizeUnitCode(item.unitCode) }}</v-chip>
                </v-col>
                <v-col cols="3" class="text-right pr-2">
                  <span v-if="item.mapped" class="success--text font-weight-medium">
                    {{ item.selectedArticle ? item.selectedArticle.item.name : '' }}
                  </span>
                  <span v-else class="grey--text">
                    Nije mapirano
                  </span>
                </v-col>
                <v-col cols="1" class="text-right">
                  <v-btn
                    icon
                    x-small
                    @click.stop="openQuickMapModal(index)"
                    color="primary"
                    v-if="!item.mapped"
                  >
                    <v-icon small>mdi-magnify</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-row class="mt-2">
                <!-- Left: XML data (read-only) -->
                <v-col cols="5">
                  <v-card outlined class="pa-3">
                    <v-card-subtitle class="pa-0 pb-2 font-weight-bold">
                      Podaci iz e-računa
                    </v-card-subtitle>
                    <v-simple-table dense>
                      <tbody>
                        <tr>
                          <td class="font-weight-medium">Naziv:</td>
                          <td>{{ item.name }}</td>
                        </tr>
                        <tr v-if="item.sellersItemId">
                          <td class="font-weight-medium">Šifra dobavljača:</td>
                          <td>{{ item.sellersItemId }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Količina:</td>
                          <td>{{ item.quantity }} {{ normalizeUnitCode(item.unitCode) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Jed. cijena (bez PDV):</td>
                          <td>{{ formatCurrency(item.unitPrice) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Jed. cijena (s PDV):</td>
                          <td>{{ formatCurrency(item.priceWithTax) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">PDV stopa:</td>
                          <td>{{ item.taxRate }}%</td>
                        </tr>
                        <tr v-if="item.discount > 0">
                          <td class="font-weight-medium">Rabat:</td>
                          <td>{{ formatCurrency(item.discount) }}</td>
                        </tr>
                        <tr v-if="item.chargesTotal > 0">
                          <td class="font-weight-medium">Troškovi:</td>
                          <td>{{ formatCurrency(item.chargesTotal) }}</td>
                        </tr>
                        <tr v-if="item.depositFee > 0">
                          <td class="font-weight-medium">Povratna naknada:</td>
                          <td>{{ formatCurrency(item.depositFee) }}</td>
                        </tr>
                        <tr>
                          <td class="font-weight-medium">Ukupno:</td>
                          <td class="font-weight-bold">{{ formatCurrency(item.lineTotal) }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card>
                </v-col>

                <!-- Right: Warehouse article search + editable fields -->
                <v-col cols="7">
                  <v-card outlined class="pa-3">
                    <v-card-subtitle class="pa-0 pb-2 font-weight-bold">
                      Mapiranje na skladišni artikl
                    </v-card-subtitle>

                    <!-- Article search -->
                    <v-autocomplete
                      v-model="item.selectedArticle"
                      :items="item.searchResults"
                      :search-input.sync="item.searchQuery"
                      :loading="item.searching"
                      no-filter
                      label="Odaberite artikl iz skladišta *"
                      item-text="item.name"
                      return-object
                      clearable
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Počnite tipkati za pretragu (min 3 znaka)"
                      @change="onArticleSelected(index)"
                      @update:search-input="onSearchInputChange(index, $event)"
                      class="mb-3"
                    >
                      <template v-slot:item="{ item: searchItem }">
                        <v-list-item-content>
                          <v-list-item-title>{{ searchItem.item.name }}</v-list-item-title>
                          <v-list-item-subtitle>
                            Šifra: {{ searchItem.item.code || '-' }} |
                            Stanje: {{ formatStock(searchItem.item.stock) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>

                    <!-- Editable fields -->
                    <v-row dense>
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="item.editableQuantity"
                          label="Količina *"
                          type="number"
                          step="0.001"
                          outlined
                          dense
                          hide-details="auto"
                          :rules="[v => v > 0 || 'Količina mora biti veća od 0']"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-select
                          v-model="item.editableUnitCode"
                          :items="unitOptionsFor(item)"
                          label="Mjerna jedinica"
                          outlined
                          dense
                          hide-details="auto"
                        ></v-select>
                      </v-col>
                    </v-row>

                    <v-row dense class="mt-2">
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="item.editablePriceBezPdv"
                          label="Jed. cijena (bez PDV) *"
                          type="number"
                          step="0.01"
                          outlined
                          dense
                          hide-details="auto"
                          :rules="[v => v >= 0 || 'Cijena ne može biti negativna']"
                          @input="recalculatePriceWithTax(index)"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="item.editablePriceSPdv"
                          label="Jed. cijena (s PDV)"
                          type="number"
                          step="0.01"
                          outlined
                          dense
                          hide-details="auto"
                          readonly
                          disabled
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-row dense class="mt-2">
                      <v-col cols="6">
                        <v-select
                          v-model.number="item.editableTaxRate"
                          :items="taxRateOptions"
                          label="PDV stopa % *"
                          outlined
                          dense
                          hide-details="auto"
                          @change="recalculatePriceWithTax(index)"
                        ></v-select>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="item.editableDiscount"
                          label="Rabat %"
                          type="number"
                          step="0.01"
                          outlined
                          dense
                          hide-details="auto"
                          :rules="[v => v <= 100 || 'Rabat ne može biti veći od 100']"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-row dense class="mt-2">
                      <v-col cols="12">
                        <v-text-field
                          v-model.number="item.editableDepositFee"
                          label="Povratna naknada"
                          type="number"
                          step="0.01"
                          outlined
                          dense
                          hide-details="auto"
                          :rules="[v => v >= 0 || 'Povratna naknada ne može biti negativna']"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn text @click="cancel" color="error">
          <v-icon left>mdi-close</v-icon>
          Odustani
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="applyMapping"
          :disabled="!allItemsMapped"
          depressed
          large
        >
          <v-icon left>mdi-check</v-icon>
          Primijeni {{ mappedCount }} artikala
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Quick Map Modal -->
    <v-dialog v-model="showQuickMapModal" max-width="600px" @keydown.esc="closeQuickMapModal">
      <v-card v-if="quickMapItem">
        <v-card-title class="primary white--text">
          {{ quickMapItem.name }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeQuickMapModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-autocomplete
            ref="quickSearchInput"
            v-model="quickMapSelectedArticle"
            :items="quickMapSearchResults"
            :search-input.sync="quickMapSearchQuery"
            :loading="quickMapSearching"
            no-filter
            label="Pretražite i odaberite artikl *"
            item-text="item.name"
            return-object
            autofocus
            outlined
            hide-details="auto"
            placeholder="Počnite tipkati za pretragu (min 3 znaka)"
            @keydown.enter="confirmQuickMap"
            class="mb-3"
          >
            <template v-slot:item="{ item: searchItem }">
              <v-list-item-content>
                <v-list-item-title>{{ searchItem.item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  Šifra: {{ searchItem.item.code || '-' }} | Stanje: {{ formatStock(searchItem.item.stock) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <v-alert type="info" dense text class="mb-0">
            <small>Enter za potvrdu | Esc za zatvaranje</small>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="confirmQuickMap" :disabled="!quickMapSelectedArticle">
            <v-icon left>mdi-check</v-icon>
            Potvrdi
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { debounce } from 'lodash'
import { df } from '@/plugins/firebase'
import { collection, query, where, limit, getDocs } from 'firebase/firestore'
import trigram from '@/mixins/trigram'
import { splitLines, toPurchaseLine } from '../einvoice/lines'
import { autoMatchBySupplierCode } from '../einvoice/supplierCodes'

export default {
  name: 'ItemMappingDialog',
  mixins: [trigram],
  props: {
    items: {
      type: Array,
      required: true
    },
    locations: {
      type: Array,
      required: true
    },
    warehouses: {
      type: Array,
      default: () => []
    },
    initialLocationId: {
      type: String,
      default: null
    },
    initialWarehouseId: {
      type: String,
      default: null
    },
    supplier: {
      type: Object,
      default: null
    },
    companyId: {
      type: String,
      default: ''
    },
    hasDocumentDeposit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialogVisible: true,
      openPanels: [0], // Open first panel by default
      localItems: [],
      depositLines: [],
      selectedLocation: null,
      selectedWarehouse: null,
      taxRateOptions: [
        { text: '0%', value: 0 },
        { text: '5%', value: 5 },
        { text: '13%', value: 13 },
        { text: '25%', value: 25 }
      ],
      // Keyboard navigation
      focusedIndex: 0,
      // Quick map modal
      showQuickMapModal: false,
      quickMapItemIndex: null,
      quickMapItem: null,
      quickMapSearchQuery: '',
      quickMapSearchResults: [],
      quickMapSearching: false,
      quickMapSelectedArticle: null
    }
  },
  computed: {
    mappedCount () {
      return this.localItems.filter(item => item.mapped).length
    },
    allItemsMapped () {
      return this.localItems.length > 0 && this.localItems.every(item => item.mapped)
    }
  },
  watch: {
    selectedWarehouse (warehouse) {
      if (!warehouse) {
        this.debouncedSearch.cancel()
        this.debouncedQuickSearch.cancel()
        return
      }
      this.autoMapItems()
      const first = this.localItems[0]
      if (first && first.searchQuery && !first.mapped) this.searchArticles(0)
    },
    supplier () {
      this.localItems.forEach(item => {
        if (!item.mappedManually) item.wasAmbiguous = false
      })
      this.autoMapItems()
    },
    quickMapSearchQuery (newVal) {
      if (newVal && newVal.length >= 3) {
        this.debouncedQuickSearch()
      } else {
        this.quickMapSearchResults = []
      }
    }
  },
  mounted () {
    // Setup keyboard shortcuts
    window.addEventListener('keydown', this.handleKeydown)

    // Initialize location and warehouse from props
    if (this.initialLocationId && this.locations.length > 0) {
      this.selectedLocation = this.locations.find(loc => loc.id === this.initialLocationId || loc.value === this.initialLocationId)
    } else if (this.locations.length === 1) {
      this.selectedLocation = this.locations[0]
    }

    if (this.initialWarehouseId && this.warehouses.length > 0) {
      this.selectedWarehouse = this.warehouses.find(wh => wh.id === this.initialWarehouseId || wh.value === this.initialWarehouseId)
    }

    // Auto-focus first unhandled item
    this.$nextTick(() => {
      const firstUnhandled = this.localItems.findIndex(item => !item.mapped)
      if (firstUnhandled !== -1) {
        this.focusedIndex = firstUnhandled
      }
    })
  },
  beforeDestroy () {
    // Remove keyboard listener
    window.removeEventListener('keydown', this.handleKeydown)
  },
  created () {
    const { articleLines, depositLines } = splitLines(this.items)
    this.depositLines = depositLines
    this.localItems = articleLines.map(item => {
      const purchase = toPurchaseLine(item)
      return {
        ...item,
        searchQuery: item.name || '', // Pre-fill search with XML item name
        searchResults: [],
        searching: false,
        selectedArticle: null,
        mapped: false,
        mappedManually: false,
        wasAmbiguous: false,
        editableQuantity: Number.isFinite(item.quantity) ? item.quantity : 1,
        editableUnitCode: this.normalizeUnitCode(item.unitCode || 'kom'),
        editablePriceBezPdv: item.unitPrice || 0,
        editablePriceSPdv: item.priceWithTax || 0,
        editableTaxRate: purchase.stopaPdv,
        editableDiscount: purchase.rabat,
        editableDepositFee: item.depositFee || 0
      }
    })

    // Setup debounced search
    this.debouncedSearch = debounce(this.searchArticles, 300)
    this.debouncedQuickSearch = debounce(this.quickSearch, 300)
  },
  methods: {
    // Location/Warehouse selection
    onLocationChange () {
      // Reset warehouse selection when location changes
      this.selectedWarehouse = null
      // Emit event to parent to fetch warehouses for selected location
      this.$emit('location-changed', this.selectedLocation)
    },

    // Keyboard navigation
    handleKeydown (event) {
      // Don't handle if quick map modal is open
      if (this.showQuickMapModal) return

      // Don't handle if user is typing in an input
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault()
          this.moveFocusUp()
          break
        case 'ArrowDown':
          event.preventDefault()
          this.moveFocusDown()
          break
        case 'ArrowRight':
          event.preventDefault()
          this.expandFocusedPanel()
          break
        case 'ArrowLeft':
          event.preventDefault()
          this.collapseFocusedPanel()
          break
        case 'Enter':
          event.preventDefault()
          this.openQuickMapModal(this.focusedIndex)
          break
      }
    },

    moveFocusUp () {
      if (this.focusedIndex > 0) {
        this.focusedIndex--
      }
    },

    moveFocusDown () {
      if (this.focusedIndex < this.localItems.length - 1) {
        this.focusedIndex++
      }
    },

    expandFocusedPanel () {
      if (!this.openPanels.includes(this.focusedIndex)) {
        this.openPanels.push(this.focusedIndex)
      }
    },

    collapseFocusedPanel () {
      const index = this.openPanels.indexOf(this.focusedIndex)
      if (index !== -1) {
        this.openPanels.splice(index, 1)
      }
    },

    // Quick map modal
    openQuickMapModal (itemIndex) {
      this.quickMapItemIndex = itemIndex
      this.quickMapItem = this.localItems[itemIndex]
      this.quickMapSearchQuery = this.quickMapItem.name || ''
      this.quickMapSearchResults = []
      this.quickMapSelectedArticle = null
      this.showQuickMapModal = true

      // Auto-focus search input
      this.$nextTick(() => {
        if (this.$refs.quickSearchInput) {
          this.$refs.quickSearchInput.focus()
        }
      })
    },

    closeQuickMapModal () {
      this.showQuickMapModal = false
      this.quickMapItemIndex = null
      this.quickMapItem = null
      this.quickMapSearchQuery = ''
      this.quickMapSearchResults = []
      this.quickMapSelectedArticle = null
    },

    async quickSearch () {
      if (!this.quickMapSearchQuery || this.quickMapSearchQuery.length < 3) {
        this.quickMapSearchResults = []
        return
      }

      if (!this.selectedWarehouse || !this.selectedWarehouse.id) return

      try {
        this.quickMapSearching = true

        const triGramObject = this.triGram(this.quickMapSearchQuery.toLowerCase())
        const searchConstraints = []

        Object.keys(triGramObject).forEach(name => {
          searchConstraints.push(where(`item.meta.${name}`, '==', true))
        })

        searchConstraints.push(where('item.status', '==', 'OK'))
        searchConstraints.push(limit(10))

        const path = `warehouses/${this.selectedWarehouse.id}/extended_items`
        const constraints = [collection(df, path), ...searchConstraints]
        const q = query.apply(this, constraints)

        const snapshot = await getDocs(q)
        this.quickMapSearchResults = []

        snapshot.forEach((doc) => {
          this.quickMapSearchResults.push(doc.data())
        })

        this.quickMapSearchResults.sort((a, b) =>
          a.item.name.localeCompare(b.item.name)
        )
      } catch (error) {
        console.error('Error searching articles:', error)
      } finally {
        this.quickMapSearching = false
      }
    },

    confirmQuickMap () {
      if (!this.quickMapSelectedArticle) return

      const item = this.localItems[this.quickMapItemIndex]

      // Map the article
      item.selectedArticle = this.quickMapSelectedArticle
      item.mapped = true
      item.mappedManually = true

      // Close modal
      this.closeQuickMapModal()

      // Move to next unhandled item
      this.moveToNextUnhandled()
    },

    moveToNextUnhandled () {
      const nextUnhandledIndex = this.localItems.findIndex((item, idx) =>
        idx > this.focusedIndex && !item.mapped
      )

      if (nextUnhandledIndex !== -1) {
        this.focusedIndex = nextUnhandledIndex
        // Auto-open quick map for next item
        this.$nextTick(() => {
          this.openQuickMapModal(nextUnhandledIndex)
        })
      }
    },
    normalizeUnitCode (unitCode) {
      const mapping = {
        EA: 'kom',
        PCE: 'kom',
        H87: 'kom', // UN/ECE code for piece
        C62: 'kom', // UN/ECE code for one/piece
        KGM: 'kg',
        LTR: 'l',
        MTR: 'm',
        MTK: 'm2',
        MTQ: 'm3'
      }
      // Convert to uppercase for case-insensitive matching
      return mapping[unitCode?.toUpperCase()] || unitCode?.toLowerCase() || 'kom'
    },

    unitOptionsFor (item) {
      const article = item.selectedArticle && item.selectedArticle.item
      const units = article && article.warehouse_units && article.warehouse_units.units
        ? Object.values(article.warehouse_units.units).map(unit => unit.storage_unit)
        : [article && article.storage_unit]
      return [...new Set([...units, item.editableUnitCode, 'kom'].filter(Boolean))]
    },

    async autoMapItems () {
      if (!this.supplier || !this.selectedWarehouse || !this.selectedWarehouse.id) return
      const warehouse = this.selectedWarehouse
      const supplier = this.supplier
      const lookup = { companyId: this.companyId, warehouseId: warehouse.id }
      const pending = this.localItems.filter(item =>
        !item.mapped && item.sellersItemId && String(item.sellersItemId).trim())

      await Promise.all(pending.map(async item => {
        try {
          const result = await autoMatchBySupplierCode(df, lookup, supplier.id, String(item.sellersItemId).trim())
          if (this.selectedWarehouse !== warehouse || this.supplier !== supplier) return
          if (result.status === 'ambiguous') item.wasAmbiguous = true
          if (result.status !== 'matched' || item.mapped) return
          item.selectedArticle = result.item
          item.searchResults = [result.item]
          item.mapped = true
          item.mappedManually = false
        } catch (error) {
          console.error('Error auto-mapping article:', error)
        }
      }))
    },

    onSearchInputChange (itemIndex, value) {
      // Trigger debounced search when user types
      if (value && value.length >= 3) {
        this.debouncedSearch(itemIndex)
      } else if (!value || value.length === 0) {
        // Clear results if search is cleared
        this.localItems[itemIndex].searchResults = []
      }
    },

    async searchArticles (itemIndex) {
      const item = this.localItems[itemIndex]
      const searchQuery = item.searchQuery

      if (!searchQuery || searchQuery.length < 3) {
        item.searchResults = []
        return
      }

      if (!this.selectedWarehouse || !this.selectedWarehouse.id) return

      try {
        item.searching = true

        // Use the same trigram method as Kalkulacija
        const triGramObject = this.triGram(searchQuery.toLowerCase())

        const searchConstraints = []

        // Add trigram constraints (same as Kalkulacija)
        Object.keys(triGramObject).forEach(name => {
          searchConstraints.push(where(`item.meta.${name}`, '==', true))
        })

        searchConstraints.push(where('item.status', '==', 'OK'))
        searchConstraints.push(limit(10))

        const path = `warehouses/${this.selectedWarehouse.id}/extended_items`

        const constraints = [collection(df, path), ...searchConstraints]
        const q = query.apply(this, constraints)

        const snapshot = await getDocs(q)

        item.searchResults = []

        snapshot.forEach((doc) => {
          const data = doc.data()
          item.searchResults.push(data)
        })

        item.searchResults.sort((a, b) =>
          a.item.name.localeCompare(b.item.name)
        )
      } catch (error) {
        console.error('Error searching articles:', error)
        // Emit error to parent component
        this.$emit('error', 'Greška pri pretraživanju artikala')
      } finally {
        item.searching = false
      }
    },

    onArticleSelected (itemIndex) {
      const item = this.localItems[itemIndex]

      if (item.selectedArticle) {
        item.mapped = true
        item.mappedManually = true

        // Auto-expand next unhandled item
        const nextUnhandledIndex = this.localItems.findIndex((it, idx) =>
          idx > itemIndex && !it.mapped
        )

        if (nextUnhandledIndex !== -1) {
          if (!this.openPanels.includes(nextUnhandledIndex)) {
            this.openPanels.push(nextUnhandledIndex)
          }
        }
      } else {
        item.mapped = false
      }
    },

    recalculatePriceWithTax (itemIndex) {
      const item = this.localItems[itemIndex]
      const priceBezPdv = parseFloat(item.editablePriceBezPdv) || 0
      const taxRate = parseFloat(item.editableTaxRate) || 0

      item.editablePriceSPdv = parseFloat((priceBezPdv * (1 + taxRate / 100)).toFixed(5))
    },

    formatCurrency (amount) {
      if (amount === null || amount === undefined) return '0.00 €'
      return `${parseFloat(amount).toFixed(2)} €`
    },

    formatStock (stock) {
      if (!stock) return '0'
      return (stock / 1000).toFixed(2)
    },

    applyMapping () {
      // Validate all items are mapped
      if (!this.allItemsMapped) {
        // Emit error to parent component
        this.$emit('error', 'Molimo mapirajte sve artikle prije nastavka.')
        return
      }

      const mappedItems = this.localItems
        .filter(item => item.mapped)
        .map(item => {
          const article = item.selectedArticle

          const parsedQuantity = parseFloat(item.editableQuantity)
          const kolicina = Number.isFinite(parsedQuantity) ? parsedQuantity : 1
          const jedinicnaCijenaBezPdv = parseFloat(item.editablePriceBezPdv) || 0
          const rabat = parseFloat(item.editableDiscount) || 0
          const ukupnaCijenaBezPdv = Math.round((kolicina * jedinicnaCijenaBezPdv * (1 - rabat / 100) + Number.EPSILON) * 100) / 100

          const mappedItem = {
            id: article.id,
            artikl: article.item.name,
            code: article.item.code,
            kolicina,
            jedinicnaCijenaBezPdv,
            jedinicnaCijenaSPdv: parseFloat(item.editablePriceSPdv) || 0,
            stopaPdv: parseFloat(item.editableTaxRate) || 0,
            rabat,
            ukupnaCijenaBezPdv,
            jedinicnaCijenaSRabatom: kolicina ? ukupnaCijenaBezPdv / kolicina : 0,
            povratnaNaknadaIznos: this.depositFeeFor(item, article, kolicina),
            mjernaJedinica: item.editableUnitCode,
            mjernaJedinicaNaziv: item.editableUnitCode,
            xmlSourceName: item.name,
            sellersItemId: item.sellersItemId ? String(item.sellersItemId).trim() : '',
            mappedManually: item.mappedManually,
            wasAmbiguous: item.wasAmbiguous,
            item: article.item,
            stock: article.item.stock || 0,
            lastPrice: article.item.lastPrice || 0
          }

          return mappedItem
        })

      this.$emit('complete', {
        items: mappedItems,
        depositLines: this.depositLines,
        location: this.selectedLocation,
        warehouse: this.selectedWarehouse
      })
      this.dialogVisible = false
    },

    depositFeeFor (item, article, kolicina) {
      const own = parseFloat(item.editableDepositFee) || 0
      const config = article.item.naknade && article.item.naknade.povratna
      if (own || (!this.depositLines.length && !this.hasDocumentDeposit) || !config) return own
      return Math.round(kolicina * (parseFloat(config.amount) || 0)) / 100
    },

    cancel () {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.v-expansion-panel-header {
  min-height: 56px;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 16px;
}

.success.lighten-5 {
  background-color: #e8f5e9 !important;
}

.focused-item {
  border-left: 4px solid #1577da !important;
  background-color: #f5f5f5;
}
</style>
